// Angular Files
import { Component, EventEmitter, Input, Output, HostBinding, ViewEncapsulation } from '@angular/core';

// Teller Online Library Files
import { TellerOnlineAppService } from 'libraries/core/src/lib/services/app.service';
import { TellerOnlineSiteMetadataService } from 'libraries/core/src/lib/services/site-metadata.service';

// Service interfaces for optional injection
export interface AuthServiceInterface {
    isSignedIn: boolean;
    signOut(redirectUrl?: string): Promise<void>;
}

export interface UserServiceInterface {
    currentUser?: {
        name: string;
    };
}

export interface InboundRedirectServiceInterface {
    isInboundRedirectUIContext: boolean;
    redirectSourceFriendlyName: string;
    promptForRedirectToSource(): Promise<void>;
    cancelPaymentAndRedirect(): Promise<void>;
}

export interface PaymentProcessorProviderInterface {
    canSavePaymentMethod: boolean;
}

export interface HeaderEditEvent {
    field: string;
    value: string;
}

@Component({
    selector: 'public-header',
    templateUrl: './public-portal-header.component.html',
    styleUrls: ['./public-portal-header.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class PublicPortalHeaderComponent {
    @Input() preview: boolean = false;
    
    @HostBinding('class')
    get componentClass() {
        return (this.preview ? 'public-' : '') + 'header';
    }
    
    @Input() siteTitle: string = '';
    @Input() helpOpened: boolean = false;
    @Input() appService?: TellerOnlineAppService;
    @Input() siteMetadataService?: TellerOnlineSiteMetadataService;
    @Input() authService?: AuthServiceInterface;
    @Input() userService?: UserServiceInterface;
    @Input() paymentProvider?: PaymentProcessorProviderInterface;
    @Input() inboundRedirectService?: InboundRedirectServiceInterface;
    @Input() logoSrc?: string;
    @Input() authenticationEnabled?: boolean = this.siteMetadataService?.authenticationEnabled;

    // Edit mode inputs
    @Input() editMode: boolean = false;
    @Output() editChange = new EventEmitter<HeaderEditEvent>();

    @Output() toggleMobileMenu = new EventEmitter<boolean>();
    @Output() toggleHelp = new EventEmitter<boolean>();

    @Input() returnToSenderLabel: string = "Cancel Payment";
    @Input() isCheckoutSuccessPage: boolean = false;

    constructor() {}

    onClick_signOut = async () => {
        if (this.preview || !this.authService) return;
        await this.authService.signOut("/sign-in");
    }

    onClick_toggleMobileMenu() {
        if (this.preview) return;
        this.toggleMobileMenu.emit(true);
    }

    onClick_toggleHelp() {
        if (this.preview) return;
        if(!this.appService?.currentUrl.includes("help")) this.toggleHelp.emit(true);
    }

    async onClick_cancelOrCompletePayment() {
        if (this.preview || !this.inboundRedirectService) return;
        if (this.isCheckoutSuccessPage) {
            await this.inboundRedirectService.promptForRedirectToSource();
        } else {
            await this.inboundRedirectService.cancelPaymentAndRedirect();
        }
    }

    get canSavePaymentMethod(): boolean {
        return this.paymentProvider?.canSavePaymentMethod || false;
    }

    get logoSource(): string {
        if (this.logoSrc) {
            return this.logoSrc;
        }

        // Tenant logo comes from the bucket root via api/assets/*, so it is
        // track-independent. The default logo is a build-static asset.
        return this.siteMetadataService?.hasServableTenant
            ? `api/assets/${this.siteMetadataService.tenant}/logo.png`
            : 'assets/images/logo.png';
    }

    onLogoClick() {
        if (this.editMode) {
            this.editChange.emit({ field: 'logoClick', value: '' });
        }
    }
}